import { StandardStatus } from '@zoocasa/go-search';

export type PriceHistoryListingData = {
  id: number | null;
  isRental?: boolean | null;
  price?: number | null;
  soldPrice?: number | null;
  mlsNum?: string | null;
  /** Feed the record came from — decides the listing-number prefix. */
  providerId?: number | null;
  thumbnailUrl?: string | null;
  imageDesc?: string | null;
  addressPath?: string | null;
  addedAt?: Date | string | null;
  soldAt?: Date | string | null;
  expiredAt?: Date | string | null;
  standardStatus?: StandardStatus | null;
};

export type PriceHistoryMonthGroup = {
  monthKey: string; // YYYY-MM
  records: PriceHistoryRecord[];
};

type PriceHistoryDateLike = Date | string | null | undefined;
const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

const UNKNOWN_MONTH_KEY = 'unknown';

function toDate(value: PriceHistoryDateLike): Date | null {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getMonthKey(date: Date | null): string {
  if (!date) return UNKNOWN_MONTH_KEY;

  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}`;
}

export type PriceHistoryAppreciation = {
  previousSoldPrice: number | null;
  currentSoldPrice: number | null;
  amount: number | null;
  percentage: number | null;
};

/**
 * PriceHistoryRecord Value Object
 *
 * Domain concept: "What facts do we know about one listing history row?"
 *
 * Business rules:
 * - A row carries timeline fields (status/date/price/media/navigation)
 * - Timeline event date is the latest valid date among soldAt/expiredAt/addedAt
 *
 * Computed facts:
 * - hasValue: Whether this row has a displayable price value
 * - eventDate: Canonical event date used for sorting/grouping
 * - getDaysListed: Days from addedAt to soldAt/expiredAt/now
 *
 * Usage:
 * - Timeline domain: src/domain/listing/value-objects/price-history.ts
 * - Timeline hook: src/ui/price-history/use-price-history-timeline.ts
 *
 * Examples:
 * - eventDate: Use to sort records newest-first in the timeline.
 */
export class PriceHistoryRecord {
  readonly #id: number | null;
  readonly #isRental: boolean | null;
  readonly #price: number | null;
  readonly #soldPrice: number | null;
  readonly #mlsNum: string | null;
  readonly #thumbnailUrl: string | null;
  readonly #imageDesc: string | null;
  readonly #addressPath: string | null;
  readonly #addedAt: Date | string | null;
  readonly #soldAt: Date | string | null;
  readonly #expiredAt: Date | string | null;
  readonly #standardStatus: StandardStatus | null;

  private constructor(
    id: number | null,
    isRental: boolean | null,
    price: number | null,
    soldPrice: number | null,
    mlsNum: string | null,
    thumbnailUrl: string | null,
    imageDesc: string | null,
    addressPath: string | null,
    addedAt: Date | string | null,
    soldAt: Date | string | null,
    expiredAt: Date | string | null,
    standardStatus: StandardStatus | null
  ) {
    this.#id = id;
    this.#isRental = isRental;
    this.#price = price;
    this.#soldPrice = soldPrice;
    this.#mlsNum = mlsNum;
    this.#thumbnailUrl = thumbnailUrl;
    this.#imageDesc = imageDesc;
    this.#addressPath = addressPath;
    this.#addedAt = addedAt;
    this.#soldAt = soldAt;
    this.#expiredAt = expiredAt;
    this.#standardStatus = standardStatus;
  }

  static fromListing(listing: PriceHistoryListingData): PriceHistoryRecord {
    return new PriceHistoryRecord(
      listing.id,
      listing.isRental ?? null,
      listing.price ?? null,
      listing.soldPrice ?? null,
      listing.mlsNum ?? null,
      listing.thumbnailUrl ?? null,
      listing.imageDesc ?? null,
      listing.addressPath ?? null,
      listing.addedAt ?? null,
      listing.soldAt ?? null,
      listing.expiredAt ?? null,
      listing.standardStatus ?? null
    );
  }

  get hasValue(): boolean {
    return this.#soldPrice !== null || this.#price !== null;
  }

  get id(): number | null {
    return this.#id;
  }

  get isRental(): boolean | null {
    return this.#isRental;
  }

  get soldPrice(): number | null {
    return this.#soldPrice;
  }

  get mlsNum(): string | null {
    return this.#mlsNum;
  }

  get thumbnailUrl(): string | null {
    return this.#thumbnailUrl;
  }

  get imageDesc(): string | null {
    return this.#imageDesc;
  }

  get addressPath(): string | null {
    return this.#addressPath;
  }

  get status(): StandardStatus {
    return this.#standardStatus ?? StandardStatus.STANDARD_STATUS_UNSPECIFIED;
  }

  get eventDate(): Date | null {
    const dates = [toDate(this.#soldAt), toDate(this.#expiredAt), toDate(this.#addedAt)]
      .filter((date): date is Date => date !== null);

    if (dates.length === 0) return null;

    return dates.reduce((latestDate, date) => (
      date.getTime() > latestDate.getTime() ? date : latestDate
    ));
  }

  getDaysListed(now: Date = new Date()): number | null {
    const start = toDate(this.#addedAt);
    if (!start) return null;

    const end = toDate(this.#soldAt) ?? toDate(this.#expiredAt) ?? now;
    const days = Math.floor((end.getTime() - start.getTime()) / ONE_DAY_IN_MS);

    return Math.max(days, 0);
  }
}

/**
 * PriceHistoryTimeline Value Object
 *
 * Domain concept: "How do we interpret a listing's historical timeline events?"
 *
 * Business rules:
 * - Timeline events are sorted newest first based on the best available event date
 * - Event date priority is whichever of soldAt / expiredAt / addedAt is latest
 * - Appreciation only uses non-rental sold events
 * - Days listed is computed from addedAt to soldAt/expiredAt/now
 *
 * Computed facts:
 * - hasHistory: Whether there is any true historical row beyond current listing
 * - groupByMonth: Historical rows grouped by YYYY-MM in descending order
 * - getLastSoldPrice: Latest sold price in timeline
 * - getAppreciationFromSoldHistory: Delta and percentage between two most recent sold rows
 * - getAverageDaysListed: Mean days listed across rows with valid start dates
 *
 * Usage:
 * - UI hook: src/ui/price-history/use-price-history-timeline.ts
 *
 * Examples:
 * - groupByMonth: Build month sections for timeline rendering.
 * - getAppreciationFromSoldHistory: Show appreciation stat in top cards.
 *
 * Notes:
 * - Timeline works on PriceHistoryRecord instances (record-first model).
 * - Raw listing history data should be normalized into records before timeline use.
 */
export class PriceHistoryTimeline {
  readonly #records: readonly PriceHistoryRecord[];

  private constructor(records: readonly PriceHistoryRecord[]) {
    this.#records = records;
  }

  static from(records: readonly PriceHistoryRecord[]): PriceHistoryTimeline {
    return new PriceHistoryTimeline(records);
  }

  // compare records to see which is newer
  static compareRecordsDesc(
    a: PriceHistoryRecord,
    b: PriceHistoryRecord
  ): number {
    return (b.eventDate?.getTime() ?? Number.NEGATIVE_INFINITY)
      - (a.eventDate?.getTime() ?? Number.NEGATIVE_INFINITY);
  }

  hasHistory(currentListingId: number | null): boolean {
    return this.#records.some(record => record.id !== currentListingId);
  }

  groupByMonth(): PriceHistoryMonthGroup[] {
    const sortedRecords = [...this.#records].sort(PriceHistoryTimeline.compareRecordsDesc);
    const groupsByMonth = new Map<string, PriceHistoryRecord[]>();

    sortedRecords.forEach(record => {
      const monthKey = getMonthKey(record.eventDate);
      groupsByMonth.set(monthKey, [...(groupsByMonth.get(monthKey) ?? []), record]);
    });

    return Array.from(groupsByMonth, ([monthKey, groupRecords]) => ({
      monthKey,
      records: groupRecords,
    }));
  }

  getLastSoldPrice(): number | null {
    const latestSoldEntry = [...this.#records]
      .filter(record => typeof record.soldPrice === 'number' && record.soldPrice > 0)
      .sort((a, b) => (
        (b.eventDate?.getTime() ?? Number.NEGATIVE_INFINITY)
        - (a.eventDate?.getTime() ?? Number.NEGATIVE_INFINITY)
      ))[0];

    return latestSoldEntry?.soldPrice ?? null;
  }

  getAppreciationFromSoldHistory(): PriceHistoryAppreciation {
    const soldEntries = [...this.#records]
      .filter(record => (
        typeof record.soldPrice === 'number' &&
        record.soldPrice > 0 &&
        record.isRental !== true
      ))
      .sort((a, b) => (
        (b.eventDate?.getTime() ?? Number.NEGATIVE_INFINITY)
        - (a.eventDate?.getTime() ?? Number.NEGATIVE_INFINITY)
      ));

    const currentSoldPrice = soldEntries[0]?.soldPrice ?? null;
    const previousSoldPrice = soldEntries[1]?.soldPrice ?? null;

    if (currentSoldPrice === null || previousSoldPrice === null) {
      return {
        previousSoldPrice,
        currentSoldPrice,
        amount: null,
        percentage: null,
      };
    }

    const amount = currentSoldPrice - previousSoldPrice;

    return {
      previousSoldPrice,
      currentSoldPrice,
      amount,
      percentage: (amount / previousSoldPrice) * 100,
    };
  }

  getAverageDaysListed(now: Date = new Date()): number | null {
    const days = this.#records
      .map(record => record.getDaysListed(now))
      .filter((value): value is number => value !== null);

    if (days.length === 0) return null;

    return days.reduce((sum, value) => sum + value, 0) / days.length;
  }
}
